import { LabelSchema, BodyTextSchema, PRIMITIVE_STRING_MAX, PRIMITIVE_FORM_MAX_FIELDS } from './chunk-DEPQ2OSO.js';
import { z } from 'zod';

// src/widget/primitives/flow-bounds.ts
var FLOW_MAX_STEPS = 8;
var FLOW_MAX_TOTAL_STEPS = 24;
var FLOW_MAX_BRANCH_DEPTH = 3;
var FLOW_PARSE_DEPTH_CEILING = 64;
var FLOW_MAX_BRANCH_CASES = 10;
var FLOW_MAX_TOTAL_FIELDS = 160;
var FLOW_MAX_FIELDS_PER_STEP = 20;

// src/widget/primitives/flow-depth-guard.ts
var isRecord = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
function measureFlowBranchDepth(value) {
  const steps = isRecord(value) ? value.steps : void 0;
  if (!Array.isArray(steps)) return 0;
  let deepest = 0;
  const nodes = [...steps];
  const depths = nodes.map(() => 0);
  while (nodes.length > 0) {
    const step = nodes.pop();
    const depth = depths.pop() ?? 0;
    if (depth > deepest) deepest = depth;
    if (deepest > FLOW_PARSE_DEPTH_CEILING) return deepest;
    const branch = isRecord(step) ? step.branch : void 0;
    if (!isRecord(branch)) continue;
    const push = (children) => {
      if (!Array.isArray(children)) return;
      for (const child of children) {
        nodes.push(child);
        depths.push(depth + 1);
      }
    };
    if (Array.isArray(branch.cases)) {
      for (const branchCase of branch.cases) {
        if (isRecord(branchCase)) push(branchCase.steps);
      }
    }
    push(branch.default);
  }
  return deepest;
}
function flowBranchDepthMessage(depth) {
  return `this flow nests branches at least ${String(depth)} levels deep \u2014 too deep to read. Branches may nest at most ${String(FLOW_MAX_BRANCH_DEPTH)} levels.`;
}

// src/widget/primitives/branch-key.ts
function normalizeBranchKey(value) {
  return value.normalize("NFC");
}
var FormFieldNameSchema = /* @__PURE__ */ z.string().min(1).max(80).regex(/^[a-zA-Z][a-zA-Z0-9_]*$/);
function toFormFieldName(siteFieldName) {
  const mapped = siteFieldName.replace(/[^A-Za-z0-9_]/gu, "_");
  return /^[a-zA-Z]/u.test(mapped) ? mapped : `f_${mapped}`;
}
function canonicalFieldKey(fieldName) {
  return fieldName.normalize("NFC").toLowerCase().replace(/[^a-z0-9]/gu, "");
}
var FormFieldTypeSchema = /* @__PURE__ */ z.enum([
  "text",
  "textarea",
  "email",
  "number",
  "select",
  "radio",
  "checkbox",
  "date",
  "datetime",
  "range"
]);
var ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
var ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
var SELECT_MAX_OPTIONS = 50;
var SelectOptionValueSchema = /* @__PURE__ */ z.string().min(1).max(200);
var SelectOptionSchema = /* @__PURE__ */ z.object({
  value: SelectOptionValueSchema,
  label: LabelSchema
});
var BaseFieldSchema = /* @__PURE__ */ z.object({
  /** Stable field name used as the submission payload key. */
  name: FormFieldNameSchema,
  label: LabelSchema,
  /** Optional helper text shown below the input. */
  helper: BodyTextSchema.optional(),
  /** Optional placeholder for empty-state hint inside the input. */
  placeholder: z.string().max(200).optional(),
  required: z.boolean().default(false),
  /**
   * SYN-206/SYN-217 — marks a field whose value is sensitive (tax id,
   * credential-like input). Three protections key on it (ADR-0180):
   * text/email inputs render password-masked on screen; the value is masked
   * (`•••`) at every operator-facing read/export seam (dashboard transcript,
   * insights, and the generic-channel fenced-JSON bubble at composition);
   * and the tool-result row persists the marked-field list on its metadata.
   * The persisted content and the MODEL-facing value stay intact — `/resume`
   * rebuilds model context from the row, governed by the tenant's
   * conversation-persistence instruction (ADR-0175 §5). Omitted ⇒ a regular
   * visible, unmasked field (the pre-existing behaviour).
   */
  sensitive: z.boolean().optional()
});
var FormFieldSchema = /* @__PURE__ */ z.discriminatedUnion("type", [
  BaseFieldSchema.extend({
    type: z.literal("text"),
    maxLength: z.number().int().positive().max(PRIMITIVE_STRING_MAX).optional()
  }),
  BaseFieldSchema.extend({
    type: z.literal("textarea"),
    maxLength: z.number().int().positive().max(PRIMITIVE_STRING_MAX).optional(),
    rows: z.number().int().min(2).max(20).default(4)
  }),
  BaseFieldSchema.extend({
    type: z.literal("email")
  }),
  BaseFieldSchema.extend({
    type: z.literal("number"),
    min: z.number().optional(),
    max: z.number().optional()
  }),
  BaseFieldSchema.extend({
    type: z.literal("select"),
    options: z.array(SelectOptionSchema).min(1).max(SELECT_MAX_OPTIONS)
  }),
  BaseFieldSchema.extend({
    /**
     * The same choice as `select`, shown open.
     *
     * A separate type rather than a `select` display flag, because the two carry
     * different guidance and a flag would hide it: radios show every option at
     * once and cost vertical space, so they are right for a SHORT list where the
     * options are the question ("standard / express / overnight"), and wrong for
     * a long one. The cap is the enforcement — a 50-option radio group is a
     * scroll, not a choice — and it is far below `select`'s for that reason.
     */
    type: z.literal("radio"),
    options: z.array(SelectOptionSchema).min(2).max(6)
  }),
  BaseFieldSchema.extend({
    /**
     * A bounded number where the RANGE is the information.
     *
     * Distinct from `number`, and the difference is what the control SHOWS
     * rather than what it collects: a `number` field asks "how many", where the
     * answer could be 3 or 3,000 and the input says nothing about which is
     * expected; a `range` asks "where on this scale", and the scale is visible
     * before the visitor touches it. Right for a rating, a satisfaction score, a
     * budget within a known band — wrong whenever the bounds are arbitrary,
     * because a slider then invents a ceiling the question never had.
     *
     * `min`/`max` are REQUIRED for exactly that reason. An unbounded slider is
     * not a slider, and defaulting them (0..100 is the usual guess) would put a
     * scale on the screen that nobody chose.
     */
    type: z.literal("range"),
    min: z.number().finite(),
    max: z.number().finite(),
    /** Granularity. Defaults to 1 — whole numbers, which is what a rating is. */
    step: z.number().finite().positive().optional()
  }),
  BaseFieldSchema.extend({
    type: z.literal("checkbox")
  }),
  BaseFieldSchema.extend({
    /**
     * A calendar date, with no time and no timezone.
     *
     * The wire value is an ISO `YYYY-MM-DD` string — what `<input type="date">`
     * produces natively in every supported browser, and what a date means when
     * nobody said otherwise. Deliberately NOT a timestamp: "2026-08-14" is the
     * same day in Berlin and in Los Angeles, where an instant is not, and
     * silently converting one to the other is how a booking lands a day early.
     *
     * `min`/`max` bound it — the common "no dates in the past" and "within 90
     * days" cases — and are themselves ISO dates so the bound and the value are
     * the same kind of thing.
     */
    type: z.literal("date"),
    min: z.string().regex(ISO_DATE).optional(),
    max: z.string().regex(ISO_DATE).optional()
  }),
  BaseFieldSchema.extend({
    /**
     * A WALL-CLOCK date and time, with no timezone — "2026-09-14T14:30".
     *
     * The timezone question is the reason this type was held back when `date`
     * and `radio` landed (2026-08-14: "a datetime needs a timezone story before
     * it can be honest about what it collected"). The story is `date`'s, one
     * unit down. An appointment at 14:30 is at 14:30 where the appointment is,
     * and the two ways to make it an INSTANT here are both wrong: converting
     * from the visitor's device zone books the wrong hour whenever they are not
     * where the business is (a customer booking a Berlin service from a London
     * airport), and converting from the business's zone needs a fact the widget
     * does not have. So the wire value is the wall clock the visitor chose, and
     * the zone belongs to whoever executes the booking — the customer's own
     * endpoint, which knows its own calendar. It is the same decision `date`
     * made, and for the same reason: "2026-09-14" is one day everywhere, and
     * silently making it an instant is how a booking lands a day early.
     *
     * `min`/`max` are themselves wall-clock datetimes, so the bound and the
     * value are the same kind of thing, and they compare as STRINGS — exact for
     * this format, which sorts lexicographically the way it sorts
     * chronologically.
     */
    type: z.literal("datetime"),
    min: z.string().regex(ISO_DATE_TIME).optional(),
    max: z.string().regex(ISO_DATE_TIME).optional()
  })
]);
var FormFieldWithRangeCheck = FormFieldSchema.superRefine((field, ctx) => {
  if (field.type !== "range") return;
  if (field.max <= field.min) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "`max` must be above `min` \u2014 a range with no span cannot be moved",
      path: ["max"]
    });
  }
});
var FormPropsSchema = /* @__PURE__ */ z.object({
  fields: z.array(FormFieldWithRangeCheck).min(1).max(PRIMITIVE_FORM_MAX_FIELDS),
  /** Submit button label. Defaults to "Submit" at the render layer. */
  submitLabel: LabelSchema.optional(),
  /** Action descriptor that fires on successful submission. */
  submitAction: z.object({
    id: z.string().min(1).max(120)
  })
});
function validateFlowTree(flow, ctx) {
  const stepIds = /* @__PURE__ */ new Set();
  const fieldNames = /* @__PURE__ */ new Set();
  let totalSteps = 0;
  let totalFields = 0;
  const issue = (message) => {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["steps"], message });
  };
  const walkStep = (step, depth) => {
    totalSteps += 1;
    totalFields += step.fields.length;
    if (stepIds.has(step.id)) issue(`duplicate step id "${step.id}"`);
    stepIds.add(step.id);
    for (const field of step.fields) {
      if (fieldNames.has(field.name)) {
        issue(
          `duplicate field name "${field.name}" across steps \u2014 values merge into one flat payload`
        );
      }
      fieldNames.add(field.name);
    }
    const { branch } = step;
    if (branch === void 0) return;
    if (depth >= FLOW_MAX_BRANCH_DEPTH) {
      issue(
        `step "${step.id}" branches at nesting depth ${String(depth)}, exceeding the maximum of ${String(FLOW_MAX_BRANCH_DEPTH)}`
      );
    }
    const discriminator = step.fields.find((field) => field.name === branch.field);
    if (discriminator === void 0) {
      issue(
        `branch on step "${step.id}" names field "${branch.field}", which is not a field of that step`
      );
    } else if (discriminator.type !== "select") {
      issue(
        `branch on step "${step.id}" names field "${branch.field}" of type "${discriminator.type}" \u2014 a branch discriminator must be a select`
      );
    } else if (!discriminator.required) {
      issue(
        `branch discriminator "${branch.field}" on step "${step.id}" must be required: true \u2014 an unanswered discriminator has no successor steps`
      );
    }
    const optionKeys = [];
    if (discriminator?.type === "select") {
      const seen = /* @__PURE__ */ new Set();
      for (const option of discriminator.options) {
        const key = normalizeBranchKey(option.value);
        if (seen.has(key)) {
          issue(
            `branch discriminator "${branch.field}" on step "${step.id}" has duplicate option value "${option.value}"`
          );
        }
        seen.add(key);
        optionKeys.push(key);
      }
    }
    const claimed = /* @__PURE__ */ new Set();
    for (const branchCase of branch.cases) {
      for (const value of branchCase.values) {
        const key = normalizeBranchKey(value);
        if (optionKeys.length > 0 && !optionKeys.some((option) => option === key)) {
          issue(
            `branch case value "${value}" on step "${step.id}" is not a declared option of "${branch.field}"`
          );
        }
        if (claimed.has(key)) {
          issue(
            `branch case value "${value}" on step "${step.id}" appears in more than one case \u2014 each answer must select exactly one case`
          );
        }
        claimed.add(key);
      }
    }
    if (branch.default === void 0) {
      const uncovered = optionKeys.filter((option) => !claimed.has(option));
      if (uncovered.length > 0) {
        issue(
          `branch on step "${step.id}" has no default and does not cover option value(s): ${uncovered.join(", ")} \u2014 add a default or a case for each`
        );
      }
    }
    for (const branchCase of branch.cases) {
      for (const child of branchCase.steps) walkStep(child, depth + 1);
    }
    for (const child of branch.default ?? []) walkStep(child, depth + 1);
  };
  for (const step of flow.steps) walkStep(step, 0);
  const longestPath = (steps) => {
    let total = 0;
    for (const step of steps) {
      let deepestBranch = 0;
      if (step.branch !== void 0) {
        for (const branchCase of step.branch.cases) {
          deepestBranch = Math.max(deepestBranch, longestPath(branchCase.steps));
        }
        deepestBranch = Math.max(deepestBranch, longestPath(step.branch.default ?? []));
      }
      total += 1 + deepestBranch;
    }
    return total;
  };
  const pathLength = longestPath(flow.steps);
  if (pathLength > FLOW_MAX_STEPS) {
    issue(
      `the longest path through this flow is ${String(pathLength)} steps, exceeding the maximum of ${String(FLOW_MAX_STEPS)} a visitor may walk`
    );
  }
  if (totalSteps > FLOW_MAX_TOTAL_STEPS) {
    issue(
      `this flow declares ${String(totalSteps)} steps in total, exceeding the maximum of ${String(FLOW_MAX_TOTAL_STEPS)}`
    );
  }
  if (totalFields > FLOW_MAX_TOTAL_FIELDS) {
    issue(
      `this flow declares ${String(totalFields)} fields in total, exceeding the maximum of ${String(FLOW_MAX_TOTAL_FIELDS)}`
    );
  }
}

// src/widget/primitives/flow.schema.ts
var FlowStepSchema = /* @__PURE__ */ z.lazy(
  () => z.object({
    /** Stable step identifier (progress keys, analytics; unique per flow). */
    id: z.string().min(1).max(80),
    /** Optional short step heading shown above the fields. */
    title: LabelSchema.optional(),
    /** Optional step description under the heading. */
    description: BodyTextSchema.optional(),
    /** The step's inputs — the same field union the `form` primitive renders. */
    fields: z.array(FormFieldSchema).min(1).max(FLOW_MAX_FIELDS_PER_STEP),
    /**
     * ADR-0487 — optional statically-declared branching on one of this step's
     * own select fields. NOTE: no Zod `.default()` anywhere inside, so an
     * absent branch stays absent — the round-trip deep-equals the spec it was
     * handed, and the server reconstructs the spec from persisted raw args.
     */
    branch: z.object({
      /** Name of a `required: true` `select` field IN THIS STEP. */
      field: z.string().min(1).max(80),
      cases: z.array(
        z.object({
          /**
           * Option values of `field` selecting this case. Bounds IMPORT the
           * select's own (a legal option value must never be an illegal
           * case value).
           */
          values: z.array(SelectOptionValueSchema).min(1).max(SELECT_MAX_OPTIONS),
          /** Steps spliced after this step. Empty ⇒ no follow-up questions. */
          steps: z.array(FlowStepSchema).max(FLOW_MAX_STEPS)
        })
      ).min(1).max(FLOW_MAX_BRANCH_CASES),
      /** Else-arm. Required unless the cases cover every declared option. */
      default: z.array(FlowStepSchema).max(FLOW_MAX_STEPS).optional()
    }).optional()
  })
);
var FlowPropsSchema = /* @__PURE__ */ z.object({
  /** Optional flow heading shown above the progress indicator. */
  title: LabelSchema.optional(),
  /**
   * A SAFETY valve, guarded HERE because the `superRefine` below only runs
   * once the parse has succeeded — and past ~500 levels the parse itself
   * overflows the stack, turning `safeParse` into a THROW. Both entry points
   * read `safeParse().success` to decide between a model retry and dropping
   * a frame, so a throw is a crash where a refusal was designed. See
   * `flow-depth-guard.ts` for the full story.
   *
   * The ceiling is far above `FLOW_MAX_BRANCH_DEPTH` on purpose: an ordinary
   * over-nesting mistake must still reach the refine, which names the
   * offending step in a message the model retries from and a test pins.
   *
   * Deliberately a `preprocess` on this FIELD rather than a wrapper around
   * the whole object: `FlowPropsSchema.innerType().omit()` in `apps/api`'s
   * `PresentUserFlowArgsSchema` needs the outer type to stay a `ZodObject`
   * carrying exactly one effect.
   */
  steps: z.preprocess((value, ctx) => {
    const depth = measureFlowBranchDepth({ steps: value });
    if (depth > FLOW_PARSE_DEPTH_CEILING) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: flowBranchDepthMessage(depth) });
      return z.NEVER;
    }
    return value;
  }, z.array(FlowStepSchema).min(1).max(FLOW_MAX_STEPS)),
  /** Final-step submit button label. Defaults to localized "Submit". */
  submitLabel: LabelSchema.optional(),
  /**
   * Action descriptor dispatched with the merged values when the flow is
   * rendered through the generic primitive channel. Optional: on the
   * `present_flow` pause path the submission rides the tool-result
   * round-trip instead, keyed by the originating tool call id.
   */
  submitAction: z.object({ id: z.string().min(1).max(120) }).optional()
}).superRefine(validateFlowTree);
var NonEmptyId = /* @__PURE__ */ z.string().min(1).max(120);
var ASK_USER_QUESTION_TOOL_NAME = "ask_user_question";
var GATHER_INPUTS_TOOL_NAME = "gather_inputs";
var CLARIFICATION_MAX_QUESTIONS = 4;
var CLARIFICATION_MAX_OPTIONS = 8;
var ClarificationOptionSchema = /* @__PURE__ */ z.object({
  value: z.string().min(1).max(200),
  label: z.string().min(1).max(200),
  description: z.string().min(1).max(500).optional()
});
var ClarificationQuestionSchema = /* @__PURE__ */ z.object({
  question: z.string().min(1).max(2e3),
  header: z.string().min(1).max(60).optional(),
  options: z.array(ClarificationOptionSchema).min(2).max(CLARIFICATION_MAX_OPTIONS),
  allowOther: z.boolean(),
  multiSelect: z.boolean(),
  /**
   * SYN-813 — the values this question should open PRESELECTED with.
   *
   * The case it exists for: the visitor read a pending action's arguments on the
   * confirm card, saw one they never chose, and asked to adjust them. The model
   * re-asks through this card carrying the values it already had, so the visitor
   * changes the one that is wrong instead of re-entering all of them.
   *
   * Rendering rules (the widget's, enforced there rather than here, because they
   * depend on the question's own `options`/`allowOther`/`multiSelect`): an entry
   * matching an option `value` opens that option checked (single-select takes the
   * FIRST match only); an entry matching nothing seeds the "Other" free-text field
   * when `allowOther`, and is IGNORED otherwise — there is no control it could
   * fill, and inventing one would answer a question the visitor never saw.
   *
   * OPTIONAL, deliberately — unlike `allowOther`/`multiSelect`, which are always
   * declared because they change how the card RENDERS. Absent here means "no
   * prefill", which is the whole prior behaviour, so there is nothing for a
   * producer to declare and nothing for the widget to guess.
   */
  initialValues: z.array(z.string().min(1).max(200)).max(8).optional()
});
var AskQuestionSchema = /* @__PURE__ */ z.object({
  type: z.literal("ask_question"),
  questionId: NonEmptyId,
  questions: z.array(ClarificationQuestionSchema).min(1).max(CLARIFICATION_MAX_QUESTIONS)
});
var QuestionAnswerItemSchema = /* @__PURE__ */ z.object({
  selectedValues: z.array(z.string().min(1).max(200)).max(8).default([]),
  selectedLabels: z.array(z.string().min(1).max(200)).max(8).default([]),
  otherText: z.string().min(1).max(2e3).optional()
}).refine((a) => a.selectedValues.length > 0 || a.otherText !== void 0, {
  message: "a question answer must carry at least one selected value or an otherText"
}).refine((a) => a.selectedLabels.length === a.selectedValues.length, {
  message: "selectedLabels must be parallel to selectedValues"
});
var QuestionAnswerSchema = /* @__PURE__ */ z.object({
  questionId: NonEmptyId,
  answers: z.array(QuestionAnswerItemSchema).min(1).max(CLARIFICATION_MAX_QUESTIONS)
});
var PRESENT_USER_FLOW_TOOL_NAME = "present_user_flow";
var PresentFlowSchema = /* @__PURE__ */ z.object({
  type: z.literal("present_flow"),
  flowId: NonEmptyId,
  flow: FlowPropsSchema,
  /**
   * Server bookkeeping for a NARROWING form (ADR-0594 slice 2): the server
   * composed this form to narrow a record set that was too large for one
   * choice card, and the visitor's submission must re-dispatch the READ tool
   * named here (with the submitted filters merged onto `args`) rather than the
   * capability call the form rides under. Server-authored only — the widget
   * renders `flow` and never reads this; the transcript accumulator persists
   * it on the tool-call row so `floored-card-redispatch.ts` can aim the
   * re-dispatch. Absent on every model-presented flow.
   */
  redispatchTarget: z.object({
    toolName: z.string().min(1).max(200),
    args: z.record(z.string(), z.unknown())
  }).optional()
});
var FlowSubmissionSchema = /* @__PURE__ */ z.object({
  flowId: NonEmptyId,
  values: z.record(
    z.string().min(1).max(80),
    z.union([z.string().max(1e4), z.number(), z.boolean()])
  ),
  // Bounds mirror the field-name schema (≤80 chars) and the flow-wide field
  // ceiling, imported rather than re-derived (ADR-0487 — it was three
  // hand-copied `160` literals before).
  sensitiveFields: z.array(z.string().min(1).max(80)).max(FLOW_MAX_TOTAL_FIELDS).optional(),
  // A visitor walks at most FLOW_MAX_STEPS steps, so the path is bounded by the
  // same constant the schema's longest-path refine enforces.
  path: z.array(z.string().min(1).max(80)).max(FLOW_MAX_STEPS).optional()
});

export { ASK_USER_QUESTION_TOOL_NAME, AskQuestionSchema, CLARIFICATION_MAX_OPTIONS, CLARIFICATION_MAX_QUESTIONS, ClarificationOptionSchema, ClarificationQuestionSchema, FLOW_MAX_BRANCH_CASES, FLOW_MAX_BRANCH_DEPTH, FLOW_MAX_FIELDS_PER_STEP, FLOW_MAX_STEPS, FLOW_MAX_TOTAL_FIELDS, FLOW_MAX_TOTAL_STEPS, FLOW_PARSE_DEPTH_CEILING, FlowPropsSchema, FlowStepSchema, FlowSubmissionSchema, FormFieldNameSchema, FormFieldSchema, FormFieldTypeSchema, FormFieldWithRangeCheck, FormPropsSchema, GATHER_INPUTS_TOOL_NAME, NonEmptyId, PRESENT_USER_FLOW_TOOL_NAME, PresentFlowSchema, QuestionAnswerItemSchema, QuestionAnswerSchema, SELECT_MAX_OPTIONS, SelectOptionValueSchema, canonicalFieldKey, flowBranchDepthMessage, measureFlowBranchDepth, normalizeBranchKey, toFormFieldName };
//# sourceMappingURL=chunk-EG6ISLSR.js.map
//# sourceMappingURL=chunk-EG6ISLSR.js.map